import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { FaGraduationCap, FaBriefcase, FaCode, FaHeart } from 'react-icons/fa';

const AboutSection = () => {
  const { t } = useTranslation();

  // Przykładowe dane - należy zastąpić własnymi informacjami
  const education = [
    {
      id: 1,
      degree: 'Inżynier Informatyk',
      institution: 'Uniwersytet WSB Merito w Gdańsku',
      years: '2020 - 2025',
      description: 'Specjalizacja: Programowanie Fullstack',
    },
  ];

  const experience = [
    {
      id: 1,
      position: 'Specjalista ds. systemów informatycznych, administrator systemów MDM, inżynier oprogramowania do celów wewnętrznych firmy',
      company: 'Integrated Solutions sp. z o.o. (spółka Orange Polska)',
      years: '2019 - obecnie',
      description: '',
    },
  ];

  const skills = [
    'JavaScript', 'TypeScript', 'HTML', 'CSS', 'SCSS', 'Python', 'C#', '.NET',
    'React.js', 'Angular', 'Node.js', 'Express.js', 'Next.js', 
    'React-Native', 'Docker', 'PostgreSQL', 'MongoDB', 'Mongoose', 'Prisma', 
    'REST API', 'GraphQL', 'MySQL', 'Git', 'GitHub', 'Azure', 'Jest', 
    'Agile', 'Scrum', 'Gradle', 'Babel', 'Wzorce projektowe', 'Clean Code',
    'Tworzenie dokumentacji', 'Proxmox', 'Ubuntu Server', 'Nginx'
  ];

  const interests = [
    'Simracing', 'Motorsport', 'Programowanie Fullstack',
    'Agenci AI', 'Rozwój LLM i RAG', 'Nowe technologie', 'Sztuczna inteligencja',
    'Cyberbezpieczeństwo', 'Gry komputerowe'
  ];

  return (
    <AboutContainer id="about">
      <div className="container">
        <div className="section-title">
          <h2>{t('about.title')}</h2>
        </div>
        
        <AboutContent>
          <AboutIntro>
            <p>{t('about.intro')}</p>
          </AboutIntro>
          
          <CoreCompetencies>
            <h3>{t('about.competencies_title')}</h3>
            <p>{t('about.competencies_text')}</p>
          </CoreCompetencies>
          
          <AboutGrid>
            <AboutCard>
              <AboutCardHeader>
                <AboutCardIcon>
                  <FaGraduationCap />
                </AboutCardIcon>
                <AboutCardTitle>{t('about.education')}</AboutCardTitle>
              </AboutCardHeader>
              <AboutCardContent>
                {education.map(item => (
                  <TimelineItem key={item.id}>
                    <TimelineHeader>
                      <TimelineTitle>{item.degree}</TimelineTitle>
                      <TimelineSubtitle>{item.institution}</TimelineSubtitle>
                      <TimelineYears>{item.years}</TimelineYears>
                    </TimelineHeader>
                    <TimelineDescription>{item.description}</TimelineDescription>
                  </TimelineItem>
                ))}
              </AboutCardContent>
            </AboutCard>
            
            <AboutCard>
              <AboutCardHeader>
                <AboutCardIcon>
                  <FaBriefcase />
                </AboutCardIcon>
                <AboutCardTitle>{t('about.experience')}</AboutCardTitle>
              </AboutCardHeader>
              <AboutCardContent>
                {experience.map(item => (
                  <TimelineItem key={item.id}>
                    <TimelineHeader>
                      <TimelineTitle>{item.position}</TimelineTitle>
                      <TimelineSubtitle>{item.company}</TimelineSubtitle>
                      <TimelineYears>{item.years}</TimelineYears>
                    </TimelineHeader>
                    <TimelineDescription>{item.description}</TimelineDescription>
                  </TimelineItem>
                ))}
              </AboutCardContent>
            </AboutCard>
            
            <AboutCard>
              <AboutCardHeader>
                <AboutCardIcon>
                  <FaCode />
                </AboutCardIcon>
                <AboutCardTitle>{t('about.skills')}</AboutCardTitle>
              </AboutCardHeader>
              <AboutCardContent>
                <SkillsGrid>
                  {skills.map((skill, index) => (
                    <SkillItem key={index}>{skill}</SkillItem>
                  ))}
                </SkillsGrid>
              </AboutCardContent>
            </AboutCard>
            
            <AboutCard>
              <AboutCardHeader>
                <AboutCardIcon>
                  <FaHeart />
                </AboutCardIcon>
                <AboutCardTitle>{t('about.interests')}</AboutCardTitle>
              </AboutCardHeader>
              <AboutCardContent>
                <InterestsList>
                  {interests.map((interest, index) => (
                    <InterestItem key={index}>{interest}</InterestItem>
                  ))}
                </InterestsList>
              </AboutCardContent>
            </AboutCard>
          </AboutGrid>
        </AboutContent>
      </div>
    </AboutContainer>
  );
};

const AboutContainer = styled.section`
  padding: 5rem 0;
  background-color: transparent;
  position: relative;
`;

const AboutContent = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 3rem;
`;

const AboutIntro = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  
  p {
    font-size: 1.25rem;
    line-height: 1.6;
    color: var(--text-color);
  }
`;

const CoreCompetencies = styled.div`
  max-width: 800px;
  margin: 0 auto 2rem;
  text-align: center;
  padding: 2rem;
  background-color: var(--gray-100);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);

  h3 {
    color: var(--primary-color);
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.7;
    color: var(--text-light);
    margin: 0;
  }
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const AboutCard = styled.div`
  background-color: var(--gray-100);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  transition: transform var(--transition-normal) ease, box-shadow var(--transition-normal) ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-lg);
  }
`;

const AboutCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.5rem;
  background-color: var(--primary-color);
  color: var(--black);
`;

const AboutCardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  font-size: 1.25rem;
`;

const AboutCardTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
`;

const AboutCardContent = styled.div`
  padding: 1.5rem;
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 1.5rem;
  
  &:not(:last-child) {
    border-bottom: 1px solid var(--gray-700);
    margin-bottom: 1.5rem;
  }
`;

const TimelineHeader = styled.div`
  margin-bottom: 0.75rem;
`;

const TimelineTitle = styled.h4`
  margin: 0 0 0.25rem;
  font-size: 1.125rem;
  color: var(--text-color);
`;

const TimelineSubtitle = styled.h5`
  margin: 0 0 0.25rem;
  font-size: 1rem;
  font-weight: 500;
  color: var(--text-light);
`;

const TimelineYears = styled.span`
  display: inline-block;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--black);
  background-color: var(--primary-color);
  padding: 0.25rem 0.75rem;
  border-radius: var(--border-radius-sm);
`;

const TimelineDescription = styled.p`
  margin: 0;
  font-size: 1rem;
  color: var(--text-light);
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const SkillItem = styled.div`
  background-color: var(--gray-100);
  color: var(--text-color);
  padding: 0.5rem 0.75rem;
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  transition: all var(--transition-fast) ease;
  
  &:hover {
    background-color: var(--primary-color);
    color: var(--white);
    transform: translateY(-2px);
  }
`;

const InterestsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const InterestItem = styled.div`
  background-color: var(--gray-100);
  color: var(--text-color);
  padding: 0.5rem 0.75rem;
  border-radius: var(--border-radius-md);
  font-size: 0.875rem;
  font-weight: 500;
  transition: all var(--transition-fast) ease;
  
  &:hover {
    background-color: var(--primary-color);
    color: var(--white);
    transform: translateY(-2px);
  }
`;

export default AboutSection;